import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { 
  LogOut, 
  ArrowLeft, 
  Mail, 
  MailOpen,
  Search,
  ExternalLink,
  Trash2,
  Clock,
  CheckCircle,
  Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminInbox = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState([]);
  const [filteredInquiries, setFilteredInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [notes, setNotes] = useState('');
  const [stats, setStats] = useState({ total: 0, pending: 0 });

  useEffect(() => {
    loadInquiries();
  }, []);

  useEffect(() => {
    filterInquiries();
  }, [inquiries, statusFilter, searchQuery]);

  const loadInquiries = async () => {
    try {
      const response = await axios.get(`${API}/admin/inquiries`);
      setInquiries(response.data.inquiries);
      setStats({
        total: response.data.total,
        pending: response.data.pending_count
      });
    } catch (error) {
      console.error('Failed to load inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const filterInquiries = () => {
    let filtered = [...inquiries];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inq => inq.status === statusFilter);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(inq => 
        inq.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        inq.subject.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredInquiries(filtered);
  };

  const updateStatus = async (inquiryId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${inquiryId}`, {
        status: newStatus,
        notes: notes || undefined
      });
      toast.success(`Inquiry marked as ${newStatus}`);
      loadInquiries();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(null);
        setNotes('');
      }
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const deleteInquiry = async (inquiryId) => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/inquiries/${inquiryId}`);
      toast.success('Inquiry deleted');
      loadInquiries();
      if (selectedInquiry?.id === inquiryId) {
        setSelectedInquiry(null);
      }
    } catch (error) {
      console.error('Failed to delete inquiry:', error);
      toast.error('Failed to delete inquiry');
    }
  };

  const openEmailClient = (inquiry) => {
    const subject = encodeURIComponent(`Re: ${inquiry.subject}`);
    const body = encodeURIComponent(`Hi ${inquiry.name},\n\nThank you for contacting RouteFlow AI.\n\n`);
    window.location.href = `mailto:${inquiry.email}?subject=${subject}&body=${body}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex items-center justify-center'>
        <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600'></div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-4'>
              <Button variant='ghost' size='icon' onClick={() => navigate('/admin')}>
                <ArrowLeft className='w-5 h-5' />
              </Button>
              <div className='flex items-center space-x-3'>
                <img 
                  src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                  alt='RouteFlow AI' 
                  className='h-10 w-auto'
                />
                <span className='text-xl font-bold gradient-text'>Admin Inbox</span>
              </div>
            </div>
            <Button variant='ghost' onClick={onLogout}>
              <LogOut className='w-4 h-4 mr-2' /> Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        {/* Header & Stats */}
        <div className='mb-6'>
          <div className='flex justify-between items-center mb-4'>
            <div>
              <h1 className='text-3xl font-bold text-gray-900 mb-2'>Inbox</h1>
              <p className='text-gray-600'>Manage customer inquiries and support requests</p>
            </div>
            <div className='flex space-x-4'>
              <Card className='border-0 shadow-md'>
                <CardContent className='p-4 flex items-center space-x-3'>
                  <div className='w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center'>
                    <Clock className='w-5 h-5 text-white' />
                  </div>
                  <div>
                    <p className='text-2xl font-bold text-gray-900'>{stats.pending}</p>
                    <p className='text-sm text-gray-600'>Pending</p>
                  </div>
                </CardContent>
              </Card>
              <Card className='border-0 shadow-md'>
                <CardContent className='p-4 flex items-center space-x-3'>
                  <div className='w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center'>
                    <Mail className='w-5 h-5 text-white' />
                  </div>
                  <div>
                    <p className='text-2xl font-bold text-gray-900'>{stats.total}</p>
                    <p className='text-sm text-gray-600'>Total</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Filters & Search */}
          <div className='flex space-x-4 mb-6'>
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5' />
              <Input
                type='text'
                placeholder='Search by name, email, or subject...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className='pl-10'
              />
            </div>
            <div className='flex space-x-2'>
              <Button 
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('all')}
              >
                All ({inquiries.length})
              </Button>
              <Button 
                variant={statusFilter === 'pending' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('pending')}
              >
                Pending ({stats.pending})
              </Button>
              <Button 
                variant={statusFilter === 'replied' ? 'default' : 'outline'}
                onClick={() => setStatusFilter('replied')}
              >
                Replied ({stats.total - stats.pending})
              </Button>
            </div>
          </div>
        </div>

        {/* Inquiries List */}
        <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
          {/* List View */}
          <div className='lg:col-span-2 space-y-4'>
            {filteredInquiries.length > 0 ? (
              filteredInquiries.map((inquiry) => (
                <Card 
                  key={inquiry.id} 
                  className={`border-0 shadow-md cursor-pointer hover:shadow-lg smooth-transition ${
                    selectedInquiry?.id === inquiry.id ? 'ring-2 ring-emerald-500' : ''
                  }`}
                  onClick={() => {
                    setSelectedInquiry(inquiry);
                    setNotes(inquiry.notes || '');
                  }}
                >
                  <CardContent className='p-6'>
                    <div className='flex justify-between items-start mb-3'>
                      <div className='flex items-center space-x-3'>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          inquiry.status === 'pending' 
                            ? 'bg-amber-100' 
                            : 'bg-emerald-100'
                        }`}>
                          {inquiry.status === 'pending' ? (
                            <Mail className='w-5 h-5 text-amber-600' />
                          ) : (
                            <MailOpen className='w-5 h-5 text-emerald-600' />
                          )}
                        </div>
                        <div>
                          <h3 className='font-semibold text-gray-900'>{inquiry.name}</h3>
                          <p className='text-sm text-gray-600'>{inquiry.email}</p>
                        </div>
                      </div>
                      <Badge variant={inquiry.status === 'pending' ? 'default' : 'secondary'}>
                        {inquiry.status}
                      </Badge>
                    </div>
                    <h4 className='font-medium text-gray-900 mb-2'>{inquiry.subject}</h4>
                    <p className='text-sm text-gray-600 line-clamp-2 mb-3'>{inquiry.message}</p>
                    <div className='flex justify-between items-center text-xs text-gray-500'>
                      <span>{formatDate(inquiry.created_at)}</span>
                      <span className='text-emerald-600 hover:text-emerald-700'>View Details →</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className='border-0 shadow-md'>
                <CardContent className='p-12 text-center'>
                  <Mail className='w-16 h-16 mx-auto mb-4 text-gray-400' />
                  <p className='text-lg text-gray-600 mb-2'>No inquiries found</p>
                  <p className='text-sm text-gray-500'>
                    {searchQuery ? 'Try adjusting your search' : 'All caught up!'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Detail View */}
          <div className='lg:col-span-1'>
            {selectedInquiry ? (
              <Card className='border-0 shadow-lg sticky top-8'>
                <CardHeader>
                  <div className='flex justify-between items-start'>
                    <div>
                      <CardTitle>{selectedInquiry.name}</CardTitle>
                      <CardDescription className='mt-1'>{selectedInquiry.email}</CardDescription>
                    </div>
                    <Badge variant={selectedInquiry.status === 'pending' ? 'default' : 'secondary'}>
                      {selectedInquiry.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className='space-y-4'>
                    {/* Subject */}
                    <div>
                      <p className='text-sm font-semibold text-gray-700 mb-1'>Subject</p>
                      <p className='text-gray-900'>{selectedInquiry.subject}</p>
                    </div>

                    {/* Message */}
                    <div>
                      <p className='text-sm font-semibold text-gray-700 mb-1'>Message</p>
                      <p className='text-gray-700 text-sm leading-relaxed whitespace-pre-wrap'>
                        {selectedInquiry.message}
                      </p>
                    </div>

                    {/* Timestamps */}
                    <div className='bg-gray-50 rounded-lg p-3 space-y-2'>
                      <div className='flex justify-between text-sm'>
                        <span className='text-gray-600'>Received</span>
                        <span className='text-gray-900 font-medium'>
                          {formatDate(selectedInquiry.created_at)}
                        </span>
                      </div>
                      {selectedInquiry.replied_at && (
                        <div className='flex justify-between text-sm'>
                          <span className='text-gray-600'>Replied</span>
                          <span className='text-gray-900 font-medium'>
                            {formatDate(selectedInquiry.replied_at)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Notes */}
                    <div>
                      <p className='text-sm font-semibold text-gray-700 mb-2'>Internal Notes</p>
                      <Textarea
                        placeholder='Add internal notes (visible only to admins)...'
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        className='resize-none'
                      />
                    </div>

                    {/* Actions */}
                    <div className='space-y-2 pt-4 border-t'>
                      <Button
                        className='w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                        onClick={() => openEmailClient(selectedInquiry)}
                      >
                        <ExternalLink className='w-4 h-4 mr-2' />
                        Reply via Email
                      </Button>
                      
                      {selectedInquiry.status === 'pending' ? (
                        <Button
                          variant='outline'
                          className='w-full'
                          onClick={() => updateStatus(selectedInquiry.id, 'replied')}
                        >
                          <CheckCircle className='w-4 h-4 mr-2' />
                          Mark as Replied
                        </Button>
                      ) : (
                        <Button
                          variant='outline'
                          className='w-full'
                          onClick={() => updateStatus(selectedInquiry.id, 'pending')}
                        >
                          <Clock className='w-4 h-4 mr-2' />
                          Mark as Pending
                        </Button>
                      )}

                      <Button
                        variant='outline'
                        className='w-full text-red-600 hover:text-red-700 hover:bg-red-50'
                        onClick={() => deleteInquiry(selectedInquiry.id)}
                      >
                        <Trash2 className='w-4 h-4 mr-2' />
                        Delete Inquiry
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className='border-0 shadow-lg'>
                <CardContent className='p-12 text-center'>
                  <Mail className='w-16 h-16 mx-auto mb-4 text-gray-400' />
                  <p className='text-gray-600'>Select an inquiry to view details</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInbox;
